import { useEffect, useRef } from "react"
import { useAudioSettings } from "@/lib/audio-settings"

const DEFAULT_VOLUME = 0.3

interface UseScreenMusicOptions {
  musicUrl: string
  isActive: boolean
  volume?: number
}

/**
 * Simple hook for playing looping background music on specific screens.
 * Plays when isActive is true, stops when false.
 */
export function useScreenMusic({
  musicUrl,
  isActive,
  volume = DEFAULT_VOLUME,
}: UseScreenMusicOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const hasUserInteracted = useRef(false)
  // Global music volume setting scales the screen's base volume
  const { music: musicSetting } = useAudioSettings()

  // Track user interaction to enable audio playback
  useEffect(() => {
    const handleInteraction = () => {
      hasUserInteracted.current = true
      // Try to play if conditions are met
      if (isActive && audioRef.current?.paused) {
        audioRef.current.play().catch(() => {})
      }
    }

    window.addEventListener("click", handleInteraction, { once: false })
    window.addEventListener("touchstart", handleInteraction, { once: false })
    window.addEventListener("keydown", handleInteraction, { once: false })

    return () => {
      window.removeEventListener("click", handleInteraction)
      window.removeEventListener("touchstart", handleInteraction)
      window.removeEventListener("keydown", handleInteraction)
    }
  }, [isActive])

  // Initialize audio element
  useEffect(() => {
    const audio = new Audio(musicUrl)
    audio.loop = true
    audio.volume = volume * musicSetting
    audioRef.current = audio

    return () => {
      audio.pause()
      audio.src = ""
      audioRef.current = null
    }
  }, [musicUrl])

  // Update volume when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume * musicSetting
    }
  }, [volume, musicSetting])

  // Handle play/pause based on active state
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isActive) {
      if (hasUserInteracted.current) {
        audio.play().catch(() => {
          // Autoplay was prevented, will play on next user interaction
        })
      }
    } else {
      audio.pause()
    }
  }, [isActive])
}
